import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  ClickAwayListener,
} from '@mui/material';
import {
  Dashboard,
  People,
  Business,
  Description,
  Send,
  Article,
  Folder,
  InsertDriveFile,
  Group,
  Warehouse,
  Menu as MenuIcon,
  Security,
  VpnKey,
  Logout,
  ContactSupport,
  Assignment,
  Storage,
  KeyboardArrowDown,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useState, useRef } from 'react';
import axiosInstance from '../api/axiosConfig';
import { useAuth } from '../hooks/useAuth';
import { NotificationPanel } from '../modules/notifications';
import ImpersonationBanner from '../components/ImpersonationBanner';
import { getAvatarConfig, isAvatarUrl } from '../utils/avatarUtils';

const drawerWidth = 256;

// Sidebar color tokens
const SIDEBAR = {
  bg: '#ffffff',
  hover: 'rgba(2,81,140,0.06)',
  active: '#02518C',
  activeBorder: '#288FC6',
  text: '#64748B',
  textActive: '#ffffff',
  textHover: '#153250',
  divider: '#E2E8F0',
  icon: '#94A3B8',
  iconActive: '#ffffff',
};

const MainLayout = () => {
  const { user, logout, isOwner, isModuleEnabled } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleSettings = () => {
    handleMenuClose();
    if (user?.companyId) navigate(`/companies/${user.companyId}`);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    clearTimeout(searchTimeoutRef.current);
    if (value.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const response = await axiosInstance.get('/search', { params: { q: value } });
        setSearchResults(response.data?.data || []);
        setSearchOpen(true);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 400);
  };

  const handleSearchSelect = (item) => {
    setSearchQuery('');
    setSearchOpen(false);
    setSearchResults([]);
    if (item.type === 'correspondence') navigate(`/correspondences/${item.id}`);
    else if (item.type === 'proceeding') navigate(`/proceedings/${item.id}`);
    else if (item.type === 'document') navigate(`/documents/${item.id}`);
    else if (item.type === 'box') navigate(`/warehouses/boxes/${item.id}`);
    else if (item.type === 'thread') navigate(`/correspondences/${item.correspondenceId}`);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const hasPermission = (permission) => {
    if (!permission) return true;
    if (!user?.allPermissions) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    return user.allPermissions.includes(permission);
  };

  const menuGroups = [
    {
      label: 'Principal',
      items: [
        { text: 'Dashboard', icon: <Dashboard sx={{ fontSize: 18 }} />, path: '/dashboard', permission: null },
      ],
    },
    {
      label: 'Gestión',
      items: [
        { text: 'Empresas', icon: <Business sx={{ fontSize: 18 }} />, path: isOwner ? '/companies' : `/companies/${user?.companyId}`, permission: 'company.view' },
        { text: 'Correspondencia', icon: <Send sx={{ fontSize: 18 }} />, path: '/correspondences', permission: 'correspondence.view', module: 'correspondences' },
        { text: 'Plantillas', icon: <Article sx={{ fontSize: 18 }} />, path: '/templates', permission: 'template.view', module: 'templates' },
        { text: 'Expedientes', icon: <Folder sx={{ fontSize: 18 }} />, path: '/proceedings', permission: 'proceeding.view', module: 'proceedings' },
        { text: 'Dashboard Doc.', icon: <InsertDriveFile sx={{ fontSize: 18 }} />, path: '/documents/dashboard', permission: 'document.dashboard', module: 'documents' },
        { text: 'Retención', icon: <Description sx={{ fontSize: 18 }} />, path: '/retentions', permission: 'retention.view', module: 'retentions' },
        { text: 'Externos', icon: <Group sx={{ fontSize: 18 }} />, path: '/entities', permission: 'entity.view', module: 'entities' },
        { text: 'Almacenes', icon: <Warehouse sx={{ fontSize: 18 }} />, path: '/warehouses', permission: 'warehouse.view', module: 'warehouses' },
      ],
    },
    {
      label: 'Diagnóstico',
      items: [
        { text: 'Instrumentos', icon: <Assignment sx={{ fontSize: 18 }} />, path: '/surveys', permission: null, ownerOnly: true },
        { text: 'Resultados DX', icon: <InsertDriveFile sx={{ fontSize: 18 }} />, path: '/surveys/results', permission: null, ownerOnly: true },
        { text: 'Custodia Digital', icon: <Storage sx={{ fontSize: 18 }} />, path: '/custodia', permission: null, ownerOnly: true },
      ],
    },
    {
      label: 'Administración',
      items: [
        { text: 'Permisos', icon: <VpnKey sx={{ fontSize: 18 }} />, path: '/permissions', permission: 'permission.view' },
        { text: 'Roles', icon: <Security sx={{ fontSize: 18 }} />, path: '/roles', permission: 'role.view' },
        { text: 'Usuarios', icon: <People sx={{ fontSize: 18 }} />, path: '/users', permission: 'user.view' },
        { text: 'Soporte', icon: <ContactSupport sx={{ fontSize: 18 }} />, path: '/support', permission: null, ownerOnly: true },
        { text: 'Admin Tickets', icon: <Assignment sx={{ fontSize: 18 }} />, path: '/support/admin', permission: null, ownerOnly: true },
      ],
    },
  ];

  const isActive = (path) =>
    location.pathname === path ||
    (path !== '/dashboard' && location.pathname.startsWith(path));

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: SIDEBAR.bg }}>
      {/* Logo */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          borderBottom: `1px solid ${SIDEBAR.divider}`,
          minHeight: 64,
        }}
        onClick={() => navigate('/')}
      >
        <Box
          component="img"
          src="/simplia.png"
          alt="Simplia"
          sx={{
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </Box>

      {/* Nav Groups */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2, px: 2 }}>
        {menuGroups.map((group) => {
          const visibleItems = group.items.filter((item) =>
            hasPermission(item.permission) &&
            (!item.ownerOnly || isOwner) &&
            isModuleEnabled(item.module)
          );
          if (visibleItems.length === 0) return null;
          return (
            <Box key={group.label} sx={{ mb: 1 }}>
              <Typography
                sx={{
                  color: SIDEBAR.icon,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  px: 1.5,
                  mb: 0.5,
                  mt: 1,
                }}
              >
                {group.label}
              </Typography>
              {visibleItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      sx={{
                        borderRadius: 1.5,
                        py: 0.9,
                        px: 1.5,
                        minHeight: 40,
                        bgcolor: active ? SIDEBAR.active : 'transparent',
                        borderLeft: active ? `2px solid ${SIDEBAR.activeBorder}` : '2px solid transparent',
                        '&:hover': {
                          bgcolor: active ? SIDEBAR.active : SIDEBAR.hover,
                          '& .nav-text': { color: active ? SIDEBAR.textActive : SIDEBAR.textHover },
                          '& .nav-icon': { color: active ? SIDEBAR.iconActive : '#02518C' },
                        },
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <ListItemIcon
                        className="nav-icon"
                        sx={{
                          minWidth: 32,
                          color: active ? SIDEBAR.iconActive : SIDEBAR.icon,
                          transition: 'color 0.15s',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        className="nav-text"
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.8375rem',
                          fontWeight: active ? 600 : 500,
                          color: active ? SIDEBAR.textActive : SIDEBAR.text,
                          sx: { transition: 'color 0.15s' },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </Box>
          );
        })}
      </Box>

      {/* User card at bottom */}
      <Box
        sx={{
          px: 2,
          py: 2,
          borderTop: `1px solid ${SIDEBAR.divider}`,
        }}
      >
        {(() => {
          const avatarIsUrl = isAvatarUrl(user?.avatar);
          const avatarConfig = avatarIsUrl ? null : getAvatarConfig(user?.avatar);
          const AvatarIcon = avatarConfig?.icon;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: SIDEBAR.hover },
                transition: 'background-color 0.15s',
              }}
              onClick={handleMenuOpen}
            >
              <Avatar src={avatarIsUrl ? user.avatar : undefined} sx={{ width: 32, height: 32, bgcolor: avatarIsUrl ? 'grey.500' : avatarConfig?.color, flexShrink: 0 }}>
                {!avatarIsUrl && AvatarIcon && <AvatarIcon sx={{ fontSize: 18 }} />}
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.name || 'Usuario'}
                </Typography>
                <Typography sx={{ color: SIDEBAR.icon, fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.company?.name || ''}
                </Typography>
              </Box>
              <KeyboardArrowDown sx={{ color: SIDEBAR.icon, fontSize: 16, flexShrink: 0 }} />
            </Box>
          );
        })()}
      </Box>
    </Box>
  );

  const avatarIsUrl = isAvatarUrl(user?.avatar);
  const avatarConfig = avatarIsUrl ? null : getAvatarConfig(user?.avatar);
  const AvatarIcon = avatarConfig?.icon;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#ffffff',
          borderBottom: '1px solid #E2E8F0',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', gap: 1 }}>
          <IconButton
            aria-label="Abrir menú"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ display: { sm: 'none' }, color: '#64748B' }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', px: 2, position: 'relative' }}>
            <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
              <Box sx={{ position: 'relative', width: '100%', maxWidth: 480 }}>
                <TextField
                  ref={searchRef}
                  size="small"
                  placeholder="Buscar correspondencias, expedientes, cajas, hilos..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      bgcolor: '#F8FAFC',
                      borderRadius: 2,
                      fontSize: '0.875rem',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#CBD5E1' },
                      '&.Mui-focused fieldset': { borderColor: '#02518C' },
                    },
                  }}
                />
                {searchOpen && searchResults.length > 0 && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      mt: 0.5,
                      zIndex: 9999,
                      maxHeight: 400,
                      overflowY: 'auto',
                      borderRadius: 2,
                    }}
                  >
                    {searchResults.map((item, idx) => (
                      <Box
                        key={`${item.type}-${item.id}-${idx}`}
                        onClick={() => handleSearchSelect(item)}
                        sx={{
                          px: 2,
                          py: 1.25,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          '&:hover': { bgcolor: '#F8FAFC' },
                          borderBottom: idx < searchResults.length - 1 ? '1px solid #F1F5F9' : 'none',
                        }}
                      >
                        <Chip
                          label={
                            item.type === 'correspondence' ? 'Corresp.' :
                            item.type === 'proceeding' ? 'Expediente' :
                            item.type === 'document' ? 'Documento' :
                            item.type === 'box' ? 'Caja' : 'Hilo'
                          }
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: 20,
                            bgcolor:
                              item.type === 'correspondence' ? '#EFF6FF' :
                              item.type === 'proceeding' ? '#F0FDF4' :
                              item.type === 'document' ? '#FFF7ED' :
                              item.type === 'box' ? '#F5F3FF' : '#FDF2F8',
                            color:
                              item.type === 'correspondence' ? '#2563EB' :
                              item.type === 'proceeding' ? '#16A34A' :
                              item.type === 'document' ? '#EA580C' :
                              item.type === 'box' ? '#7C3AED' : '#BE185D',
                            border: 'none',
                            flexShrink: 0,
                          }}
                        />
                        <Typography sx={{ fontSize: '0.8375rem', color: '#1E293B', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>
          </Box>

          {/* Right side: Notifications + User */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <NotificationPanel />

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                elevation: 3,
                sx: {
                  mt: 1,
                  minWidth: 220,
                  borderRadius: 2,
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                },
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid #E2E8F0',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#F8FAFC' },
                  transition: 'background-color 0.15s',
                }}
                onClick={handleProfile}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar src={avatarIsUrl ? user?.avatar : undefined} sx={{ width: 36, height: 36, bgcolor: avatarIsUrl ? 'grey.300' : avatarConfig?.color }}>
                    {!avatarIsUrl && AvatarIcon && <AvatarIcon sx={{ fontSize: 20 }} />}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1E293B' }}>
                      {user?.name || 'Usuario'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                      {user?.email || ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <MenuItem
                onClick={handleLogout}
                sx={{
                  py: 1.25,
                  px: 2,
                  color: '#EF4444',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  gap: 1.5,
                  '&:hover': { bgcolor: 'rgba(239,68,68,0.06)' },
                }}
              >
                <Logout fontSize="small" />
                Cerrar Sesión
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth, bgcolor: SIDEBAR.bg },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }} />
        <ImpersonationBanner />
        <Container
          maxWidth="xl"
          sx={{
            flexGrow: 1,
            py: 3,
            px: { xs: 2, md: 3 },
          }}
        >
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default MainLayout;
